import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import QrCodeIcon from "@mui/icons-material/QrCode";
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RoofingIcon from '@mui/icons-material/Roofing';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderIcon from '@mui/icons-material/Folder';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Link as RouterLink, useLocation, Route, Routes, useNavigate } from 'react-router-dom';
import Grid2 from '@mui/material/Grid2';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useCompany } from '../contexts/CompanyContext';
import { ProjectProvider } from '../contexts/ProjectContext';
import { CompanyProvider } from '../contexts/CompanyContext';
import SubscriptionSelectModal from './SubscriptionSelectModal';

import LeadQR from './LeadQR'

// Lazy load pages to improve performance
const MyAccount = React.lazy(() => import('../pages/MyAccount'));
const Leads = React.lazy(() => import('../pages/Leads'));
const Users = React.lazy(() => import('../pages/Users'));

const Settings = React.lazy(() => import('../pages/Settings'));
const ProjectSettings = React.lazy(() => import('../pages/ProjectSettings'));
const ContractSettings = React.lazy(() => import('../pages/ContractSettings'));
const MaterialTemplates = React.lazy(() => import('../pages/MaterialTemplates'));
const MaterialTemplateDetail = React.lazy(() => import('../pages/MaterialTemplateDetail'));
const SupplementTemplates = React.lazy(() => import('../pages/SupplementTemplates'));
const SupplementTemplateDetail = React.lazy(() => import('../pages/SupplementTemplateDetail'));
const JobCostSettings = React.lazy(() => import('../pages/JobCostSettings'));
const LeadSettings = React.lazy(() => import('../pages/LeadSettings'));

const Projects = React.lazy(() => import('../pages/Projects'));
const ProjectNew = React.lazy(() => import('../pages/ProjectNew'));
const ProjectDetail = React.lazy(() => import('../pages/ProjectDetail'));
const ProjectEdit = React.lazy(() => import('../pages/ProjectEdit'));
const Contracts = React.lazy(() => import('../pages/Contracts'));
const Materials = React.lazy(() => import('../pages/Materials'));
const MaterialDetail = React.lazy(() => import('../pages/MaterialDetail'));
const BuildSheet = React.lazy(() => import('../pages/BuildSheet'));
const JobCosts = React.lazy(() => import('../pages/JobCosts'));
const Supplements = React.lazy(() => import('../pages/Supplements'));
const Attachments = React.lazy(() => import('../pages/Attachments'));
const Gutters = React.lazy(() => import('../pages/Gutters'));
const Estimate = React.lazy(() => import('../pages/Estimate'));
const Invoice = React.lazy(() => import('../pages/Invoice'));
const Calendar = React.lazy(() => import('../pages/Calendar'));

const BuildSheetReport = React.lazy(() => import('../pages/reports/BuildSheetReport'));
const SupplementsReport = React.lazy(() => import('../pages/reports/SupplementsReport'));
const MaterialOrderForm = React.lazy(() => import('../pages/reports/MaterialOrderForm'));
const EstimateReport = React.lazy(() => import('../pages/reports/EstimateReport'));
const InvoiceReport = React.lazy(() => import('../pages/reports/InvoiceReport'));

// Extend the Theme type to include custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    coolGray: { [key: number]: string };
    amber: { [key: number]: string };
  }
  interface PaletteOptions {
    coolGray?: { [key: number]: string };
    amber?: { [key: number]: string };
  }
}

// Create a custom theme
const theme = createTheme({
  palette: {
    coolGray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    amber: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          zIndex: 0,  // Set the z-index of all input labels globally
        },
      },
    },
  },
});

const MainApp: React.FC = () => {
  const params = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [isQRDrawerOpen, setIsQRDrawerOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState<string>('Home');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);
  const [projectMenuDrawerOpen, setProjectMenuDrawerOpen] = useState<boolean>(false);
  const { logout, user } = useUser();
  const { company, update: updateCompany, getPaymentMethods } = useCompany();


  useEffect(() => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    if (pathnames.length > 0) {
      const pageMap: any = {
        'projects': "Projects",
        'contracts': "Contracts",
        'materials': 'Materials',
        'build-sheet': 'Build Sheet',
        'job-costs': 'Job Costs',
        'supplements': 'Supplements',
        'attachments': 'Attachments',
        'gutters': 'Gutters',
        'estimate': 'Estimate',
        'invoice': 'Invoice',
        'calendar': 'Calendar',
        'settings': 'Settings',
        'projects-settings': 'Projects Settings',
        'contract-settings': 'Contract Settings',
        'material-templates-settings': 'Material Templates Settings',
        'supplement-templates-settings': 'Supplement Templates Settings',
        'job-cost-settings': 'Job Cost Settings',
        'lead-settings': 'Lead Settings',
        'my-account': 'My Account',
        'leads': 'Leads',
        'users': 'Users',
      }
      const page = pathnames[pathnames.length - 1].charAt(0) + pathnames[pathnames.length - 1].slice(1)
      const pageTitle = pageMap[page];
      setPageTitle(pageTitle || 'Project Detail');
    } else {
      setPageTitle('Home');
    }
  }, [location.pathname]);

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleBackNavigation = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    
    if (pathnames.length === 0) {
      // Already at root, do nothing or go to projects
      return;
    }
    
    // Remove the last segment to go back one level
    const parentPath = '/' + pathnames.slice(0, -1).join('/');
    
    // If we're going back to root, navigate to projects instead
    if (parentPath === '/' || parentPath === '') {
      navigate('/projects');
    } else {
      navigate(parentPath);
    }
  };

  const handleLogout = () => {
    // Implement logout logic here
    console.log('Logging out...');
    logout();
    // For example, clear local storage, reset state, redirect to login page, etc.
    // navigate('/login');
  };

  const handlePlanSelected = async (planId: string) => {
    if (!company) return;
    // Store plan selection temporarily - will create Stripe subscription after payment method
    await updateCompany({ ...company, subscriptionPlan: planId });
    // Don't close modal yet - wait for payment method
  };

  const checkPaymentMethods = async () => {
    if (!company || !user?.isOwner) return;
    try {
      const methods = await getPaymentMethods();
      setHasPaymentMethod(methods && methods.length > 0);
    } catch (e) {
      console.error('Error checking payment methods:', e);
      setHasPaymentMethod(false);
    }
  };

  useEffect(() => {
    if (company && user?.isOwner) {
      checkPaymentMethods();
    }
  }, [company, user]);

  useEffect(() => {
    // Show modal if:
    // 1. No subscription plan, OR
    // 2. Has subscription plan but no payment method
    if (company && user?.isOwner && hasPaymentMethod !== null) {
      const hasPlan = Boolean(company.subscriptionPlan);
      const needsSubscription = !hasPlan || (hasPlan && !hasPaymentMethod);
      setShowSubscriptionModal(needsSubscription);
    }
  }, [company, user, hasPaymentMethod]);

  // const menuItems = [
  //   { text: 'My Account', path: '/my-account', icon: <AccountCircleIcon /> },
  //   { text: 'Leads', path: '/leads', icon: <ContactPageIcon /> },
  //   { text: 'Projects', path: '/projects', icon: <ConstructionIcon /> },
  //   { text: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  //   { text: 'Users', path: '/users', icon: <PeopleIcon /> },
  // ];

  const getMenuItems = () => {
    const menu = [
      { text: 'My Account', path: '/my-account', icon: <AccountCircleIcon /> },
      { text: 'Leads', path: '/leads', icon: <ContactPageIcon /> },
      { text: 'Projects', path: '/projects', icon: <ConstructionIcon /> },
    ]

    if (user?.role === 'admin' || user?.isOwner) {
      menu.push({ text: 'Users', path: '/users', icon: <PeopleIcon /> });
      menu.push({ text: 'Settings', path: '/settings', icon: <SettingsIcon /> });
    }
    return menu
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar 
          position="static" 
          sx={{ 
            bgcolor: theme.palette.coolGray[700], 
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <Box sx={{ mr: 2, height: 50, display: 'flex', alignItems: 'center' }}>
              <img src="/logo_colored.svg" alt="The Roofing App" style={{ width: 50, fill: theme.palette.amber[400] }} />
            </Box>
            <Box sx={{ flexGrow: 1, textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center' }}>
              {/* The Roofing App */}
              <img src="/theRoofingApp_colored.svg" alt="The Roofing App" style={{ height: '100%', fill: theme.palette.amber[400] }} />
            </Box>

            <IconButton
              edge="end"
              aria-label="qr-code"
              sx={{ color: theme.palette.amber[500], mr: 1 }}
              onClick={() => setIsQRDrawerOpen(true)} 
            >
              <QrCodeIcon />
            </IconButton>
            <IconButton
              edge="end"
              aria-label="menu"
              sx={{ color: theme.palette.amber[500] }}
              onClick={toggleDrawer(true)} 
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
        >
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
          >
            {company?.logo && (
              <>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img 
                    src={company.logo?.full || company.logo?.thumbnail || company.logo?.url || (typeof company.logo === 'string' ? company.logo : '')} 
                    alt="Company Logo" 
                    style={{ maxWidth: '100%', maxHeight: 100, objectFit: 'contain' }}
                  />
                </Box>
                <Divider />
              </>
            )}
            <List>
              {getMenuItems().map((item: any) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton component={RouterLink} to={item.path}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon /></ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>

        <Drawer
          anchor="right"
          open={isQRDrawerOpen}
          onClose={ () => setIsQRDrawerOpen(false) }>
            <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
              Lead Form QR Code
            </Typography>
            <LeadQR />
        </Drawer>

        <SubscriptionSelectModal
          open={showSubscriptionModal}
          onPlanSelected={handlePlanSelected}
          onComplete={async () => {
            await checkPaymentMethods();
            setShowSubscriptionModal(false);
          }}
        />

        <Box sx={{ padding: 2, bgcolor: theme.palette.amber[400], position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ width: 48, display: 'flex', justifyContent: 'flex-start' }}>
              {(() => {
                const pathnames = location.pathname.split('/').filter((x) => x);
                // Show back button only if there's more than one level (can go back)
                const canGoBack = pathnames.length > 1;
                
                return canGoBack ? (
                  <IconButton
                    onClick={handleBackNavigation}
                    sx={{ color: theme.palette.coolGray[700] }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                ) : null;
              })()}
            </Box>
            <Typography variant="h6" sx={{ color: theme.palette.coolGray[700], fontSize: 20 }}>
              {pageTitle}
            </Typography>
          </Box>
          
          {(() => {
            // Extract projectId from pathname if we're on a project route
            const pathMatch = location.pathname.match(/^\/projects\/([^/]+)/);
            const projectId = pathMatch ? pathMatch[1] : null;
            
            return projectId ? (
              <>
                <Button
                  variant="text"
                  onClick={() => setProjectMenuDrawerOpen(true)}
                  startIcon={<MenuOpenIcon />}
                  sx={{
                    color: theme.palette.coolGray[700],
                    textTransform: 'none',
                    textDecoration: 'none',
                    minWidth: 'auto',
                    padding: { xs: 1, sm: '6px 16px' },
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    },
                    '& .MuiButton-startIcon': {
                      margin: 0,
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Projects Menu
                  </Box>
                </Button>
                <Drawer
                  anchor="right"
                  open={projectMenuDrawerOpen}
                  onClose={() => setProjectMenuDrawerOpen(false)}
                >
                  <Box sx={{ width: 250, p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Project Menu
                    </Typography>
                    <List>
                      {[
                        { text: 'Project', icon: <FolderIcon />, path: `/projects/${projectId}` },
                        { text: 'Materials', icon: <InventoryIcon />, path: `/projects/${projectId}/materials` },
                        { text: 'Build Sheet', icon: <BuildIcon />, path: `/projects/${projectId}/build-sheet` },
                        { text: 'Job Cost', icon: <CalculateIcon />, path: `/projects/${projectId}/job-costs` },
                        { text: 'Supplements', icon: <ListAltIcon />, path: `/projects/${projectId}/supplements` },
                        { text: 'Attachments', icon: <AttachFileIcon />, path: `/projects/${projectId}/attachments` },
                        { text: 'Gutters', icon: <RoofingIcon />, path: `/projects/${projectId}/gutters` },
                        { text: 'Estimate', icon: <DescriptionIcon />, path: `/projects/${projectId}/estimate` },
                        { text: 'Invoice', icon: <DescriptionIcon />, path: `/projects/${projectId}/invoice` },
                        { text: 'Calendar', icon: <CalendarTodayIcon />, path: `/projects/${projectId}/calendar` },
                      ].map((item, index) => (
                        <React.Fragment key={item.text}>
                          <ListItemButton
                            onClick={() => {
                              navigate(item.path);
                              setProjectMenuDrawerOpen(false);
                            }}
                          >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                          </ListItemButton>
                          {index < 9 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                </Drawer>
              </>
            ) : null;
          })()}
        </Box>

        <Box sx={{ padding: 1 }}>
          <React.Suspense fallback={<Typography>Loading...</Typography>}>
            <Routes>
              {/* <Route path="/" element={<Typography>Welcome to The Roofing App</Typography>} /> */}
              <Route path="/" element={<Projects />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/users" element={<Users />} />

              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/projects-settings" element={<ProjectSettings />} />
              <Route path="/settings/contract-settings" element={<ContractSettings />} />
              <Route path="/settings/material-templates-settings" element={<MaterialTemplates />} />
              <Route path="/settings/material-templates-settings/:templateId" element={<MaterialTemplateDetail />} />
              <Route path="/settings/supplement-templates-settings" element={<SupplementTemplates />} />
              <Route path="/settings/supplement-templates-settings/:templateId" element={<SupplementTemplateDetail />} />
              <Route path="/settings/job-cost-settings" element={<JobCostSettings />} />
              <Route path="/settings/lead-settings" element={<LeadSettings />} />

              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/new" element={<ProjectNew />} />
              <Route path="/projects/:projectId" element={<ProjectProvider><ProjectDetail /></ProjectProvider>} />
              <Route path="/projects/:projectId/contracts" element={<ProjectProvider><Contracts /></ProjectProvider>} />
              <Route path="/projects/:projectId/materials" element={<ProjectProvider><Materials /></ProjectProvider>} />
              <Route path="/projects/:projectId/materials/:materialId" element={<ProjectProvider><MaterialDetail /></ProjectProvider>} />
              <Route path="/projects/:projectId/build-sheet" element={<ProjectProvider><BuildSheet /></ProjectProvider>} />
              <Route path="/projects/:projectId/job-costs" element={<ProjectProvider><JobCosts /></ProjectProvider>} />
              <Route path="/projects/:projectId/supplements" element={<ProjectProvider><Supplements /></ProjectProvider>} />
              <Route path="/projects/:projectId/attachments" element={<ProjectProvider><Attachments /></ProjectProvider>} />
              <Route path="/projects/:projectId/gutters" element={<ProjectProvider><Gutters /></ProjectProvider>} />
              <Route path="/projects/:projectId/estimate" element={<ProjectProvider><Estimate /></ProjectProvider>} />
              <Route path="/projects/:projectId/invoice" element={<ProjectProvider><Invoice /></ProjectProvider>} />
              <Route path="/projects/:projectId/calendar" element={<ProjectProvider><Calendar /></ProjectProvider>} />

              <Route path="/projects/:projectId/report/build-sheet" element={<ProjectProvider><BuildSheetReport /></ProjectProvider>} />     
              <Route path="/projects/:projectId/report/supplements" element={<ProjectProvider><SupplementsReport /></ProjectProvider>} />  
              <Route path="/projects/:projectId/report/material-order" element={<ProjectProvider><MaterialOrderForm /></ProjectProvider>} />
              <Route path="/projects/:projectId/report/estimate" element={<ProjectProvider><EstimateReport /></ProjectProvider>} />
              <Route path="/projects/:projectId/report/invoice" element={<ProjectProvider><InvoiceReport /></ProjectProvider>} />    
            </Routes>
          </React.Suspense>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MainApp;